import React, { useEffect, useMemo, useState } from 'react';

const FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'number',
  'checkbox',
  'select',
  'date',
  'tel',
  'file',
  'city_selector',
];

const FIELD_TYPE_LABELS = {
  text: 'Text',
  textarea: 'Text area',
  email: 'Email',
  number: 'Number',
  checkbox: 'Checkbox',
  select: 'Select',
  date: 'Date',
  tel: 'Phone',
  file: 'File upload',
  city_selector: 'City selector',
};

const randomId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9\s_-]/g, '')
  .replace(/[\s_]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const createDefaultField = () => ({
  id: randomId('field'),
  key: '',
  label: 'New field',
  type: 'text',
  required: false,
  placeholder: '',
  options: [],
  options_input: '',
});

const createDefaultSection = () => ({
  id: randomId('section'),
  title: 'New section',
  description: '',
  fields: [createDefaultField()],
});

const createDefaultPage = () => ({
  id: randomId('page'),
  title: 'New page',
  description: '',
  sections: [createDefaultSection()],
});

const createDefaultDraft = () => ({
  request_tab_name: '',
  form_name: '',
  description: '',
  is_published: false,
  schema: {
    version: 1,
    pages: [createDefaultPage()],
  },
});

const sanitizeField = (field) => {
  const label = String(field?.label || '').trim() || 'Field';
  const key = slugify(field?.key || label) || randomId('field_key');
  const type = FIELD_TYPES.includes(String(field?.type || '').trim()) ? String(field.type).trim() : 'text';
  const normalizedArrayOptions = Array.isArray(field?.options)
    ? field.options.map((option) => String(option || '').trim()).filter(Boolean)
    : [];

  const rawOptionsInput = (typeof field?.options_input === 'string')
    ? field.options_input
    : (typeof field?.optionsInput === 'string')
      ? field.optionsInput
      : normalizedArrayOptions.join(', ');

  const parsedOptions = String(rawOptionsInput || '')
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean);

  const options = parsedOptions.length > 0 ? parsedOptions : normalizedArrayOptions;

  return {
    id: String(field?.id || randomId('field')),
    key,
    label,
    type,
    required: Boolean(field?.required),
    placeholder: String(field?.placeholder || '').trim(),
    options: type === 'select' ? options : [],
    options_input: type === 'select' ? String(rawOptionsInput || '') : '',
  };
};

const sanitizeSection = (section) => {
  const fields = Array.isArray(section?.fields) ? section.fields.map(sanitizeField) : [];
  return {
    id: String(section?.id || randomId('section')),
    title: String(section?.title || '').trim() || 'Section',
    description: String(section?.description || '').trim(),
    fields: fields.length > 0 ? fields : [createDefaultField()],
  };
};

const sanitizePage = (page) => {
  const sections = Array.isArray(page?.sections) ? page.sections.map(sanitizeSection) : [];
  return {
    id: String(page?.id || randomId('page')),
    title: String(page?.title || '').trim() || 'Page',
    description: String(page?.description || '').trim(),
    sections: sections.length > 0 ? sections : [createDefaultSection()],
  };
};

const normalizeDraft = (value) => {
  if (!value || typeof value !== 'object') return createDefaultDraft();

  const pages = Array.isArray(value?.schema?.pages) ? value.schema.pages.map(sanitizePage) : [];

  return {
    request_tab_name: String(value.request_tab_name || value.requestTabName || '').trim(),
    form_name: String(value.form_name || value.formName || '').trim(),
    description: String(value.description || '').trim(),
    is_published: Boolean(value.is_published || value.isPublished),
    schema: {
      version: 1,
      pages: pages.length > 0 ? pages : [createDefaultPage()],
    },
  };
};

const FormBuilderModal = ({
  open,
  mode,
  initialValue,
  saving,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState(createDefaultDraft());

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeDraft(initialValue));
  }, [open, initialValue]);

  const modalTitle = useMemo(
    () => (mode === 'edit' ? 'Edit form definition' : 'Create a new form'),
    [mode]
  );

  if (!open) return null;

  const updatePage = (pageIndex, updater) => {
    setDraft((prev) => {
      const nextPages = [...prev.schema.pages];
      nextPages[pageIndex] = updater(nextPages[pageIndex]);
      return {
        ...prev,
        schema: {
          ...prev.schema,
          pages: nextPages,
        },
      };
    });
  };

  const removePage = (pageIndex) => {
    setDraft((prev) => {
      const nextPages = prev.schema.pages.filter((_, idx) => idx !== pageIndex);
      return {
        ...prev,
        schema: {
          ...prev.schema,
          pages: nextPages.length > 0 ? nextPages : [createDefaultPage()],
        },
      };
    });
  };

  const addPage = () => {
    setDraft((prev) => ({
      ...prev,
      schema: {
        ...prev.schema,
        pages: [...prev.schema.pages, createDefaultPage()],
      },
    }));
  };

  const addSection = (pageIndex) => {
    updatePage(pageIndex, (page) => ({
      ...page,
      sections: [...(Array.isArray(page.sections) ? page.sections : []), createDefaultSection()],
    }));
  };

  const removeSection = (pageIndex, sectionIndex) => {
    updatePage(pageIndex, (page) => {
      const sections = (Array.isArray(page.sections) ? page.sections : []).filter((_, idx) => idx !== sectionIndex);
      return {
        ...page,
        sections: sections.length > 0 ? sections : [createDefaultSection()],
      };
    });
  };

  const updateSection = (pageIndex, sectionIndex, updater) => {
    updatePage(pageIndex, (page) => {
      const nextSections = [...(Array.isArray(page.sections) ? page.sections : [])];
      nextSections[sectionIndex] = updater(nextSections[sectionIndex]);
      return {
        ...page,
        sections: nextSections,
      };
    });
  };

  const addField = (pageIndex, sectionIndex) => {
    updateSection(pageIndex, sectionIndex, (section) => ({
      ...section,
      fields: [...(Array.isArray(section.fields) ? section.fields : []), createDefaultField()],
    }));
  };

  const removeField = (pageIndex, sectionIndex, fieldIndex) => {
    updateSection(pageIndex, sectionIndex, (section) => {
      const fields = (Array.isArray(section.fields) ? section.fields : []).filter((_, idx) => idx !== fieldIndex);
      return {
        ...section,
        fields: fields.length > 0 ? fields : [createDefaultField()],
      };
    });
  };

  const updateField = (pageIndex, sectionIndex, fieldIndex, updater) => {
    updateSection(pageIndex, sectionIndex, (section) => {
      const fields = [...(Array.isArray(section.fields) ? section.fields : [])];
      fields[fieldIndex] = updater(fields[fieldIndex]);
      return {
        ...section,
        fields,
      };
    });
  };

  const handleSave = async () => {
    const payload = normalizeDraft(draft);
    if (!payload.form_name) {
      window.alert('Form name is required.');
      return;
    }
    if (!payload.request_tab_name) {
      window.alert('Request tab name is required.');
      return;
    }
    await onSave(payload);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide modal-zoom-in" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>{modalTitle}</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>

        <div className="modal-body">
          <div className="admin-form-2col">
            <div className="form-group">
              <label>Form name *</label>
              <input
                type="text"
                value={draft.form_name}
                onChange={(event) => setDraft((prev) => ({ ...prev, form_name: event.target.value }))}
                placeholder="Registration form"
              />
            </div>
            <div className="form-group">
              <label>Request tab name *</label>
              <input
                type="text"
                value={draft.request_tab_name}
                onChange={(event) => setDraft((prev) => ({ ...prev, request_tab_name: event.target.value }))}
                placeholder="Housing requests"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              rows={3}
              value={draft.description}
              onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Optional description visible to applicants"
            />
          </div>

          <label className="admin-form-builder-toggle">
            <input
              type="checkbox"
              checked={draft.is_published}
              onChange={(event) => setDraft((prev) => ({ ...prev, is_published: event.target.checked }))}
            />
            <span>Publish immediately after save</span>
          </label>

          <div className="admin-form-builder-pages">
            {draft.schema.pages.map((page, pageIndex) => (
              <article key={page.id} className="admin-form-builder-page">
                <div className="admin-form-builder-page-head">
                  <h4>Page {pageIndex + 1}</h4>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => removePage(pageIndex)}
                  >
                    Delete page
                  </button>
                </div>

                <div className="admin-form-2col">
                  <div className="form-group">
                    <label>Page title</label>
                    <input
                      type="text"
                      value={page.title}
                      onChange={(event) => updatePage(pageIndex, (current) => ({
                        ...current,
                        title: event.target.value,
                      }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Page description</label>
                    <textarea
                      rows={2}
                      value={page.description}
                      onChange={(event) => updatePage(pageIndex, (current) => ({
                        ...current,
                        description: event.target.value,
                      }))}
                    />
                  </div>
                </div>

                {page.sections.map((section, sectionIndex) => (
                  <section key={section.id} className="admin-form-builder-section">
                    <div className="admin-form-builder-section-head">
                      <h5>Section {sectionIndex + 1}</h5>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => removeSection(pageIndex, sectionIndex)}
                      >
                        Delete section
                      </button>
                    </div>

                    <div className="admin-form-2col">
                      <div className="form-group">
                        <label>Section title</label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(event) => updateSection(pageIndex, sectionIndex, (current) => ({
                            ...current,
                            title: event.target.value,
                          }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Section description</label>
                        <textarea
                          rows={2}
                          value={section.description}
                          onChange={(event) => updateSection(pageIndex, sectionIndex, (current) => ({
                            ...current,
                            description: event.target.value,
                          }))}
                        />
                      </div>
                    </div>

                    <div className="admin-form-builder-fields">
                      {section.fields.map((field, fieldIndex) => (
                        <div key={field.id} className="admin-form-builder-field">
                          <div className="admin-form-builder-field-grid">
                            <div className="form-group">
                              <label>Label</label>
                              <input
                                type="text"
                                value={field.label}
                                onChange={(event) => updateField(pageIndex, sectionIndex, fieldIndex, (current) => ({
                                  ...current,
                                  label: event.target.value,
                                  key: current.key || slugify(event.target.value),
                                }))}
                              />
                            </div>

                            <div className="form-group">
                              <label>Type</label>
                              <select
                                value={field.type}
                                onChange={(event) => updateField(pageIndex, sectionIndex, fieldIndex, (current) => {
                                  const nextType = event.target.value;
                                  const currentOptions = Array.isArray(current.options) ? current.options : [];
                                  const nextOptions = nextType === 'select'
                                    ? (currentOptions.length > 0 ? currentOptions : ['Option 1'])
                                    : [];
                                  const existingOptionsInput = typeof current.options_input === 'string'
                                    ? current.options_input
                                    : currentOptions.join(', ');
                                  return {
                                    ...current,
                                    type: nextType,
                                    options: nextOptions,
                                    options_input: nextType === 'select'
                                      ? (existingOptionsInput || nextOptions.join(', '))
                                      : '',
                                  };
                                })}
                              >
                                {FIELD_TYPES.map((type) => (
                                  <option key={type} value={type}>{FIELD_TYPE_LABELS[type] || type}</option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Placeholder</label>
                              <input
                                type="text"
                                value={field.placeholder || ''}
                                onChange={(event) => updateField(pageIndex, sectionIndex, fieldIndex, (current) => ({
                                  ...current,
                                  placeholder: event.target.value,
                                }))}
                              />
                            </div>
                          </div>

                          {field.type === 'select' && (
                            <div className="form-group">
                              <label>Options (comma separated)</label>
                              <input
                                type="text"
                                value={typeof field.options_input === 'string' ? field.options_input : (field.options || []).join(', ')}
                                onChange={(event) => updateField(pageIndex, sectionIndex, fieldIndex, (current) => {
                                  const rawOptions = String(event.target.value || '');
                                  const parsedOptions = rawOptions
                                    .split(',')
                                    .map((item) => item.trim())
                                    .filter(Boolean);
                                  return {
                                    ...current,
                                    options_input: rawOptions,
                                    options: parsedOptions,
                                  };
                                })}
                              />
                            </div>
                          )}

                          <div className="admin-form-builder-field-actions">
                            <label className="admin-form-builder-required-toggle">
                              <input
                                type="checkbox"
                                checked={Boolean(field.required)}
                                onChange={(event) => updateField(pageIndex, sectionIndex, fieldIndex, (current) => ({
                                  ...current,
                                  required: event.target.checked,
                                }))}
                              />
                              <span className="admin-form-builder-required-slider" aria-hidden="true" />
                              <span className="admin-form-builder-required-label">Required</span>
                            </label>

                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => removeField(pageIndex, sectionIndex, fieldIndex)}
                            >
                              Delete field
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => addField(pageIndex, sectionIndex)}
                    >
                      Add field
                    </button>
                  </section>
                ))}

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => addSection(pageIndex)}
                >
                  Add section
                </button>
              </article>
            ))}
          </div>

          <button type="button" className="btn btn-outline" onClick={addPage}>
            Add page
          </button>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save form'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilderModal;
